import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Modal, Table, TableProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';

// components
import NoData from 'components/noData/noData';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ListButton from 'components/listButton/listButton';
import ActionListHeaderComponent from '../../listHeader';
import StatusComponent from 'components/status/status';

// services

// styles

// models
import {
	onGetLoyaltyProgramStatus,
	onActivateLoyaltyProgram,
	onGetLoyaltyProgramsList,
	onCancelLoyaltyProgram,
} from '../services/loyaltyProgramService';
import NotificationService from 'core/services/notification';
import TableTotal from 'components/tableTotal/tableTotal';
import { ServiceCost, VerificationPayload } from '../../commonModels';
import { onGetServiceCost } from '../../commonServices';
import { LoyaltyProgram, LoyaltyProgramTreasuryInfo } from '../models/loyaltyProgram';
import Icon from 'utils/icon';
import VerificationModal from '../../verificationModal';
import { isRTL } from 'core/services/language';
import { StatusColors } from 'utils/statusColors';
import { formatErrorMessages } from 'utils/errorMessage';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

// components
import SuspendedTooltip from 'components/suspendedTooltip/suspendedTooltip';

interface LoyaltyProgramListPageProps {}

const LoyaltyProgramListPage: FunctionComponent<LoyaltyProgramListPageProps> = () => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const navigate = useNavigate();
	const location = useLocation();

	const [statusLoading, setStatusLoading] = useState<boolean>(true);
	const [isLocked, setIsLocked] = useState<boolean>(true);
	const [canUnlock, setCanUnlock] = useState<boolean>(false);
	const [activeGeneralSuspend, setActiveGeneralSuspend] = useState<boolean>(false);
	const [isActivating, setIsActivating] = useState<boolean>(false);
	const [verificationModalOpen, setVerificationModalOpen] = useState<boolean>(false);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [requests, setRequests] = useState<LoyaltyProgram[]>([]);

	const [serviceCostLoading, setServiceCostLoading] = useState<boolean>(true);
	const [serviceCost, setServiceCost] = useState<ServiceCost>();

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [treasuryInfo, setTreasuryInfo] = useState<LoyaltyProgramTreasuryInfo>();

	const columns: TableProps<LoyaltyProgram>['columns'] = [
		{
			title: t('ACTIONS.REQUEST_CODE'),
			dataIndex: 'request_number',
			key: 'request_number',
		},
		{
			title: t('ACTIONS.EMPLOYEE_NAME'),
			dataIndex: 'company_user',
			key: 'company_user',
			render: (company_user) => company_user.name,
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'translated_status',
			key: 'translated_status',
			render: (translated_status, row) => (
				<StatusComponent text={isRTL() ? translated_status.ar : translated_status.en} color={StatusColors[row.status]} />
			),
		},
		{
			title: t('ACTIONS.REQUEST_DATE'),
			dataIndex: 'created_at',
			key: 'date',
		},
		{
			title: t('ACTIONS.INCENTIVE_TYPE'),
			dataIndex: 'incentive_type',
			key: 'incentive_type',
			render: (incentive_type) => t('ACTIONS.' + incentive_type.toUpperCase()),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => (
				<Flex gap={20}>
					<ListButton type="view" text={t('COMMON.VIEW')} onClick={() => navigate(`view/${record.id}`)} />
					{['PendingAcceptance', 'Scheduled', 'Ongoing'].includes(record.status) ? (
						<ListButton type="reject" {...suspendedProps} text={t('COMMON.CANCEL')} onClick={() => onCancelProgram(record.id)} />
					) : null}
				</Flex>
			),
		},
	];

	const onCancelProgram = (id: number) => {
		Modal.confirm({
			title: t('ACTIONS.CONFIRM_GRANT_CANCELLATION'),
			content: t('ACTIONS.CONFIRM_GRANT_CANCELLATION_DESC'),
			okText: t('COMMON.CANCEL'),
			okButtonProps: { danger: true },
			cancelText: t('COMMON.BACK'),
			onOk: async () => {
				try {
					await onCancelLoyaltyProgram(Number(id));
					NotificationService.success(t('COMMON.SUCCESS'), t('COMMON.SUCCESS'));
					navigate('/dashboard/actions/loyalty-programs');
				} catch (error) {
					const message = formatErrorMessages(error);
					NotificationService.error(t('COMMON.ERROR'), message || t('COMMON.SUBMIT_ERROR'));
				}
			},
		});
	};

	useEffect(() => {
		setCurrentPage(getCurrentPage());
		getLoyaltyProgramStatus();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getLoyaltyProgramStatus = async () => {
		try {
			const status = await onGetLoyaltyProgramStatus();
			setIsLocked(!status.is_active);
			setCanUnlock(!status.is_free_plan && !status.has_active_general_suspend_action);
			setActiveGeneralSuspend(status.has_active_general_suspend_action);
			if (status.is_active) {
				getLoyaltyProgramsList();
				getServiceCost();
			}
			else setIsLoading(false);
		} catch (error) {
			setCanUnlock(true);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		} finally {
			setStatusLoading(false);
		}
	};

	const onActivateModule = async ({ key, method }: VerificationPayload) => {
		try {
			setIsActivating(true);
			await onActivateLoyaltyProgram({
				verified_using: method,
				[method]: key,
			});
			setVerificationModalOpen(false);
			setIsLocked(false);
			getLoyaltyProgramsList();
			getServiceCost();
		} catch (error: any) {
			const message = error.response.data.message || t('COMMON.SUBMIT_ERROR');
			NotificationService.error(t('COMMON.ERROR'), message);
		} finally {
			setIsActivating(false);
		}
	};

	const getServiceCost = async () => {
		try {
			setServiceCostLoading(true);

			// const response = await onGetServiceCost('specific_notice');
			// setServiceCost(response?.success);

			setTimeout(() => {
				setServiceCostLoading(false);
			}, 1000);
		} catch (error) {
			setServiceCostLoading(false);
		}
	};

	const getLoyaltyProgramsList = async () => {
		try {
			setIsLoading(true);
			const response = await onGetLoyaltyProgramsList(getCurrentPage());
			setRequests(response.programs.data as LoyaltyProgram[]);
			setTotalRecords(response.programs.pagination.total);
			setTreasuryInfo(response.treasury);
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetLoyaltyProgramsList(page);
			setRequests(response.programs.data as LoyaltyProgram[]);
			setTreasuryInfo(response.treasury);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	return (
		<>
			<ActionListHeaderComponent
				title={t('ACTIONS.LOYALTY_PROGRAMS')}
				subTitle={t('ACTIONS.LOYALTY_PROGRAMS_DESC')}
				addAvailable={canUnlock && !isLocked && !isLoading && treasuryInfo?.free_treasury_shares! > 0 && !activeGeneralSuspend}
				addAvailableText={activeGeneralSuspend ? t('ACTIONS.GENERAL_SUSPEND_LOYALITY_MESSAGE') : undefined}
				costLoading={statusLoading || isLoading}
				addonData={
					!isLocked &&
					!activeGeneralSuspend &&
					!isLoading && (
						<Flex align="center" vertical gap={20}>
							<StatusComponent text={t('ACTIONS.FREE_TREASURY_SHARES', { num: treasuryInfo?.free_treasury_shares ?? 0 })} color="green" />
							<StatusComponent text={t('ACTIONS.SUSPENDED_TREASURY_SHARES', { num: treasuryInfo?.suspended_treasury_shares ?? 0 })} color="red" />
						</Flex>
					)
				}
			/>

			{statusLoading ? (
				<LoadingComponent />
			) : isLocked ? (
				<>
					<Flex vertical align="center" justify="center" gap={30} className="mt-5">
						<Icon name="lockedFeature" width={150} height={150} />

						<div>
							<h3 className="text-center fw-medium">{t('ACTIONS.FEATURE_LOCKED')}</h3>

							{!canUnlock && (
								<>
									<h6 className="text-center text-light">{t('ACTIONS.FEATURE_LOCKED_DESC1')}</h6>
									<h6 className="text-center text-light">{t('ACTIONS.FEATURE_LOCKED_DESC2')}</h6>
								</>
							)}
						</div>

						<SuspendedTooltip>
							<Button type="primary" disabled={!canUnlock} style={{ fontSize: 16 }} onClick={() => setVerificationModalOpen(true)}>
								<Icon name="lock2" width={16} />
								{t('ACTIONS.UNLOCK_NOW')}
							</Button>
						</SuspendedTooltip>
					</Flex>

					<VerificationModal
						open={verificationModalOpen}
						isLoading={isActivating}
						onCancel={() => setVerificationModalOpen(false)}
						onVerificationSuccess={onActivateModule}
						module="loyalty_program_activate"
						noteType='red'
						note={t('ACTIONS.LOYALTY_PROGRAM_ACTIVATION_NOTE')}
					/>
				</>
			) : (
				<Table
					columns={columns}
					dataSource={requests}
					scroll={{ x: 600 }}
					rowKey="id"
					loading={isLoading}
					pagination={{
						pageSize: 15,
						total: totalRecords,
						showSizeChanger: false,
						current: currentPage,
						onChange: fetchMoreData,
						showTotal: (total) => <TableTotal total={total} />,
					}}
					locale={{ emptyText: <NoData /> }}
				/>
			)}
		</>
	);
};

export default LoyaltyProgramListPage;
